/**
 * Angle Role - Cast Pillar
 *
 * Normaliza input sujo para número, com detecção automática de unidade.
 *
 * @example
 * import { castAngle, castAngleStrict, detectAngleUnit } from '@attrx/role-morphic/angle/cast';
 *
 * castAngle('90°');                    // 90 (degrees)
 * castAngle('3.14159 rad', 'degree');  // ~180 (converted to degrees)
 * castAngleStrict('90');               // 90 (no conversion)
 * detectAngleUnit('45°');              // 'degree'
 */

import { ANGLE_UNITS, ANGLE_ALIASES, type AngleUnit } from "./constants";
import { convertAngle, ANGLE_BASE } from "./convert";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

// =============================================================================
// CAST
// =============================================================================

/**
 * Faz cast de input para número, com conversão opcional para unidade alvo
 *
 * @param input - Valor de entrada (number, string, etc)
 * @param targetVariant - Unidade alvo (default: degree)
 * @returns Número ou null se não puder converter
 *
 * @example
 * castAngle('90°');                    // 90
 * castAngle('3.14159 rad', 'degree');  // ~180
 * castAngle('100');                    // 100
 * castAngle(45);                       // 45
 * castAngle('invalid');                // null
 */
export function castAngle(
  input: unknown,
  targetVariant: AngleUnit = ANGLE_BASE,
): number | null {
  // Already a number
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  // String parsing
  if (typeof input === "string") {
    const parsed = parseAngleString(input);
    if (parsed === null) {
      return null;
    }

    // If unit was detected and differs from target, convert
    if (parsed.unit && parsed.unit !== targetVariant) {
      return convertAngle(parsed.unit, targetVariant, parsed.value);
    }

    return parsed.value;
  }

  return null;
}

/**
 * Faz cast sem conversão (apenas parseia o número)
 *
 * @param input - Valor de entrada
 * @returns Número ou null
 *
 * @example
 * castAngleStrict('90°');   // 90 (ignora a unidade)
 * castAngleStrict('100');   // 100
 */
export function castAngleStrict(input: unknown): number | null {
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  if (typeof input === "string") {
    const parsed = parseAngleString(input);
    return parsed?.value ?? null;
  }

  return null;
}

/**
 * Versão safe de castAngle que retorna Result
 */
export function tryCastAngle(
  input: unknown,
  targetVariant: AngleUnit = ANGLE_BASE,
): Result<number> {
  const result = castAngle(input, targetVariant);
  if (result === null) {
    return { ok: false, error: `Cannot cast "${String(input)}" to angle` };
  }
  return { ok: true, value: result };
}

// =============================================================================
// DETECT UNIT
// =============================================================================

/**
 * Detecta a unidade de ângulo de uma string
 *
 * @param input - String com valor e unidade
 * @returns Unidade detectada ou null
 *
 * @example
 * detectAngleUnit('90°');        // 'degree'
 * detectAngleUnit('3.14 rad');   // 'radian'
 * detectAngleUnit('100');        // null
 */
export function detectAngleUnit(input: string): AngleUnit | null {
  const trimmed = input.trim();

  // Special case: degree symbol can be directly attached to number
  if (trimmed.endsWith("°")) {
    return "degree";
  }

  const lowerTrimmed = trimmed.toLowerCase();

  // Extract unit part (after the number)
  const match = lowerTrimmed.match(/^[+-]?[\d.,\s]+\s*(.*)$/);
  if (!match || !match[1]) {
    return null;
  }

  const unitStr = match[1].trim();
  if (!unitStr) {
    return null;
  }

  // Check direct unit names
  if (unitStr in ANGLE_UNITS) {
    return unitStr as AngleUnit;
  }

  // Check aliases
  if (unitStr in ANGLE_ALIASES) {
    return ANGLE_ALIASES[unitStr] as AngleUnit;
  }

  // Check symbols (case-sensitive for some)
  for (const [unit, config] of Object.entries(ANGLE_UNITS)) {
    if (config.symbol.toLowerCase() === unitStr || config.symbol === unitStr) {
      return unit as AngleUnit;
    }
  }

  return null;
}

// =============================================================================
// INTERNAL HELPERS
// =============================================================================

interface ParsedAngle {
  value: number;
  unit: AngleUnit | null;
}

/**
 * Parseia string de ângulo extraindo valor e unidade
 */
function parseAngleString(input: string): ParsedAngle | null {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  // Special case: degree symbol directly attached (45°)
  const degreeMatch = trimmed.match(/^([+-]?[\d.,]+)°$/);
  if (degreeMatch) {
    const numStr = degreeMatch[1].replace(",", ".");
    const value = parseFloat(numStr);
    if (isNaN(value) || !Number.isFinite(value)) {
      return null;
    }
    return { value, unit: "degree" };
  }

  // Try to extract number and unit
  const match = trimmed.match(/^([+-]?[\d.,\s]+)\s*(.*)$/);
  if (!match) {
    return null;
  }

  const numStr = match[1].replace(/\s/g, "").replace(",", ".");
  const value = parseFloat(numStr);

  if (isNaN(value) || !Number.isFinite(value)) {
    return null;
  }

  const unitStr = match[2]?.trim().toLowerCase() || "";
  let unit: AngleUnit | null = null;

  if (unitStr) {
    // Check aliases first
    if (unitStr in ANGLE_ALIASES) {
      unit = ANGLE_ALIASES[unitStr] as AngleUnit;
    }
    // Check direct unit names
    else if (unitStr in ANGLE_UNITS) {
      unit = unitStr as AngleUnit;
    }
    // Check symbols
    else {
      for (const [u, config] of Object.entries(ANGLE_UNITS)) {
        if (config.symbol.toLowerCase() === unitStr || config.symbol === unitStr) {
          unit = u as AngleUnit;
          break;
        }
      }
    }
  }

  return { value, unit };
}
